import React from 'react';
import {
    Card,
    CardContent,
    Checkbox,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core';
import { OnwardTab, OnwardTabContainer } from '@/components/Tabs';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { colors, typography } from '@/styles';
import { PrimaryHeader } from '@/styles/typography';
import { EXCEPTION_TYPES } from '@onward-delivery/core';
import Snackbar from '@/components/Snackbar';
import {
    CenteredSpinner,
    CycleCountBreadcrumbs,
    DetailLine,
    DetailNotFound,
    FooterActionButton,
} from '@/components/CycleCounts/blocks';
import ExceptionModal from '@/components/OrderDetailsPage/modals/Exceptions';
import StorePallets from '@/components/Manifest/modals/StorePallets';
import { useCycleCountItem } from './hooks';
import { formatAddress, formatDimensions, formatQuantity, formatWeight } from './utils';

const Status = styled.span`
    ${typography.sansSerif}
    font-size: 14px;
    font-weight: 700;
    color: ${colors.greys[2]};
    border: 3px solid ${colors.greys[2]};
    border-radius: 5px;
    padding: 8px 12px;
`;

const CycleCountItem = () => {
    const {
        ids: { cycleCountId, cycleCountLocationId },
        item,
        countLocation,
        loading,
        order,
        orderItems,
        inboundManifest,
        outboundManifest,
        manifestNumber,
        isConsolidatedPallet,
        palletStatusLabel,
        palletStatusColor,
        activeTab,
        setActiveTab,
        selectedOrderItemIds,
        toggleOrderItem,
        setAllOrderItems,
        notification,
        setNotification,
        printBusy,
        editingException,
        exceptionLoading,
        relocatingPallet,
        onMarkException,
        onRelocate,
        openInManifest,
        runPrint,
        submitException,
        submitExceptionCreateAnother,
        closeException,
        storePallets,
        storePalletsInit,
        relocateOnError,
        notifyError,
    } = useCycleCountItem();

    if (loading && !item) {
        return <CenteredSpinner />;
    }

    if (!item) {
        return <DetailNotFound title="Item Not Found" message="This item could not be loaded." />;
    }

    const skuTitle = item.sku || 'Item';
    const warehouseName = countLocation?.cycle_count?.warehouse?.location_name || 'Warehouse';
    const locationTitle = countLocation?.warehouse_location || 'Location';
    const tableItems = activeTab === 'item' ? [item] : orderItems;

    return (
        <Grid
            container
            direction="column"
            css={css`
                height: 100%;
                flex-wrap: nowrap;
            `}
        >
            <Snackbar
                open={!!notification?.message}
                {...notification}
                handleClose={() => setNotification({})}
            />

            <ExceptionModal
                {...(editingException || {})}
                order={order}
                loading={exceptionLoading}
                types={[EXCEPTION_TYPES.DAMAGED, EXCEPTION_TYPES.OVERAGE, EXCEPTION_TYPES.SHORTAGE]}
                callbacks={{
                    onError: () => notifyError('Failed to save exception.'),
                    onSubmitCreateAnother: submitExceptionCreateAnother,
                    onSubmit: submitException,
                    onClose: closeException,
                }}
            />

            <StorePallets
                pallets={relocatingPallet ? [relocatingPallet] : []}
                warehouseId={item.pallet?.warehouse_id}
                loading={{}}
                callbacks={{
                    storePalletsInit,
                    setNotification,
                    onError: relocateOnError,
                    storePallets,
                }}
            />

            <Grid
                container
                direction="row"
                css={css`
                    padding: 0 1.5rem;
                    border-bottom: 1px solid ${colors.greys.border};
                    background: ${colors.white.primary};
                    align-items: center;
                    flex-wrap: nowrap;
                `}
            >
                <div
                    css={css`
                        flex: 1;
                        display: flex;
                        align-items: center;
                        flex-wrap: wrap;
                    `}
                >
                    <CycleCountBreadcrumbs
                        trail={[
                            { label: 'Cycle Counts', to: '/cycle-counts' },
                            {
                                label: warehouseName,
                                to: cycleCountId ? `/cycle-counts/${cycleCountId}` : null,
                            },
                            {
                                label: locationTitle,
                                to:
                                    cycleCountId && cycleCountLocationId
                                        ? `/cycle-counts/${cycleCountId}/locations/${cycleCountLocationId}`
                                        : null,
                            },
                            { label: skuTitle },
                        ]}
                    />
                </div>
                <OnwardTabContainer value={activeTab} onChange={(_, next) => setActiveTab(next)}>
                    <OnwardTab label="Item" value="item" />
                    <OnwardTab label="Order" value="order" />
                </OnwardTabContainer>
                <div
                    css={css`
                        flex: 1;
                    `}
                />
            </Grid>

            <Grid
                container
                direction="column"
                css={css`
                    padding: 2rem;
                    gap: 1.5rem;
                    flex-wrap: nowrap;
                    overflow-y: auto;
                    flex: 1 1 0;
                `}
            >
                {activeTab === 'item' ? (
                    <Grid
                        container
                        direction="row"
                        css={css`
                            justify-content: space-between;
                            align-items: baseline;
                        `}
                    >
                        <PrimaryHeader>{skuTitle}</PrimaryHeader>
                        <Status
                            css={css`
                                color: ${palletStatusColor};
                                border-color: ${palletStatusColor};
                            `}
                        >
                            {palletStatusLabel}
                        </Status>
                    </Grid>
                ) : (
                    <Grid container direction="row">
                        <PrimaryHeader>{order?.order_number || 'Order'}</PrimaryHeader>
                    </Grid>
                )}

                <Card>
                    <CardContent>
                        <DetailLine label="Order Number" value={order?.order_number} />
                        <DetailLine label="PO Number" value={order?.po_number} />
                        <DetailLine label="Reference Number" value={order?.reference_id} />
                        <DetailLine label="Manifest Number" value={manifestNumber} />
                        <DetailLine label="Customer" value={order?.dropoff_name} />
                        <DetailLine label="Delivery Address" value={formatAddress(order)} />
                    </CardContent>
                </Card>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {activeTab === 'order' ? (
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            color="primary"
                                            indeterminate={
                                                selectedOrderItemIds.size > 0 &&
                                                selectedOrderItemIds.size < orderItems.length
                                            }
                                            checked={
                                                orderItems.length > 0 &&
                                                selectedOrderItemIds.size === orderItems.length
                                            }
                                            onChange={(e) => setAllOrderItems(e.target.checked)}
                                        />
                                    </TableCell>
                                ) : null}
                                <TableCell>SKU</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell align="right">Quantity</TableCell>
                                <TableCell align="right">Weight (lbs)</TableCell>
                                <TableCell>Dimensions (in)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableItems.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={activeTab === 'order' ? 6 : 5} align="center">
                                        <Typography color="textSecondary">
                                            {activeTab === 'order'
                                                ? 'No items on this order.'
                                                : 'No item data.'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                tableItems.map((orderItem) => (
                                    <TableRow key={orderItem.item_id}>
                                        {activeTab === 'order' ? (
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    color="primary"
                                                    checked={selectedOrderItemIds.has(orderItem.item_id)}
                                                    onChange={(e) =>
                                                        toggleOrderItem(orderItem.item_id, e.target.checked)
                                                    }
                                                />
                                            </TableCell>
                                        ) : null}
                                        <TableCell>{orderItem.sku || '-'}</TableCell>
                                        <TableCell>{orderItem.description || '-'}</TableCell>
                                        <TableCell align="right">{formatQuantity(orderItem)}</TableCell>
                                        <TableCell align="right">{formatWeight(orderItem)}</TableCell>
                                        <TableCell>{formatDimensions(orderItem)}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>

            <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
                css={css`
                    width: auto;
                    box-shadow: 0px -4px 4px 0px #37373752;
                    min-height: 75px !important;
                    background: ${colors.white.primary};
                    flex-wrap: wrap;
                `}
            >
                {activeTab === 'item' ? (
                    <>
                        <FooterActionButton orange onClick={onMarkException}>
                            Mark Exception
                        </FooterActionButton>
                        <FooterActionButton onClick={onRelocate}>Relocate</FooterActionButton>
                        <FooterActionButton
                            onClick={() => runPrint({ itemIds: [item.item_id] })}
                            disabled={printBusy}
                        >
                            Print Item Label
                        </FooterActionButton>
                        {isConsolidatedPallet ? (
                            <FooterActionButton
                                onClick={() => runPrint({ palletIds: [item.pallet.pallet_id] })}
                                disabled={printBusy}
                            >
                                Print Pallet Label
                            </FooterActionButton>
                        ) : null}
                        <FooterActionButton
                            onClick={() => openInManifest(inboundManifest)}
                            disabled={!inboundManifest}
                        >
                            Inbound Manifest
                        </FooterActionButton>
                        <FooterActionButton
                            onClick={() => openInManifest(outboundManifest)}
                            disabled={!outboundManifest}
                        >
                            Outbound Manifest
                        </FooterActionButton>
                    </>
                ) : (
                    <FooterActionButton
                        onClick={() => runPrint({ itemIds: Array.from(selectedOrderItemIds) })}
                        disabled={printBusy || selectedOrderItemIds.size === 0}
                    >
                        Print Labels
                    </FooterActionButton>
                )}
            </Grid>
        </Grid>
    );
};

export default CycleCountItem;
